if (!customElements.get('floating-tools')) {
  class FloatingTools extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      document.documentElement.classList.add('floating-tools-enabled');
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.toggle = this.querySelector('[data-floating-toggle]');
      this.panel = this.querySelector('.floating-tools__panel');
      this.isOpen = null;
      this.setOpen(false, true);
      this.toggle?.addEventListener('click', () => this.setOpen(!this.isOpen), { signal });
      this.panel?.addEventListener('transitionend', event => {
        if (event.target === this.panel && event.propertyName === 'opacity' && !this.isOpen) this.finishClose();
      }, { signal });
      this.topButton = this.querySelector('[data-floating-top]');
      const scheduleTopVisibility = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateTopVisibility();
        });
      };
      window.addEventListener('scroll', scheduleTopVisibility, { passive: true, signal });
      window.addEventListener('resize', scheduleTopVisibility, { signal });
      this.updateTopVisibility();
      this.topButton?.addEventListener('click', () => {
        this.closeMenu();
        window.scrollTo({ top: 0, left: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, { signal });
      this.querySelector('[data-floating-support]').addEventListener('click', event => {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        this.connectChat();
        if (typeof this.chat?.show !== 'function') return;
        try {
          this.chat.show();
          event.preventDefault();
          this.closeMenu();
        } catch (error) {
          // Preserve the anchor's support-page fallback when the App cannot open.
          console.warn('Shopify Inbox could not open.', error);
          this.releaseChat();
        }
      }, { signal });
      this.querySelectorAll('[data-floating-link]').forEach(link => link.addEventListener('click', () => this.closeMenu(), { signal }));
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.closeMenu();
      }, { signal });
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) this.closeMenu();
      }, { signal });
      document.addEventListener('keydown', event => {
        if (!event.defaultPrevented && event.key === 'Escape' && this.isOpen) {
          const modalOpen = [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]')].some(dialog => dialog.getClientRects().length && getComputedStyle(dialog).visibility !== 'hidden');
          if (!this.contains(event.target) || modalOpen) {
            this.closeMenu();
            return;
          }
          event.preventDefault();
          this.closeMenu(true);
        }
      }, { signal });
      this.connectChat();
      this.chatObserver = new MutationObserver(records => {
        if (this.chat && !this.chat.isConnected) this.connectChat();
        else if (records.some(record => [...record.addedNodes].some(node => node.matches?.('shopify-chat') || node.querySelector?.('shopify-chat')))) this.connectChat();
      });
      this.chatObserver.observe(document.body, { childList: true, subtree: true });
      customElements.whenDefined('shopify-chat').then(() => {
        if (!signal.aborted) this.connectChat();
      });
    }

    updateTopVisibility() {
      const visible = window.scrollY >= window.innerHeight;
      if (this.topButton) this.topButton.hidden = !visible;
      this.dataset.topVisible = String(visible);
    }

    setOpen(open, immediate = false) {
      if (!this.toggle || !this.panel) return;
      if (this.isOpen === open) {
        if (!open && immediate) this.finishClose();
        return;
      }
      clearTimeout(this.hideTimer);
      this.isOpen = open;
      this.toggle.setAttribute('aria-expanded', String(open));
      const labels = window.ieGeekListingStrings || {};
      this.toggle.setAttribute('aria-label', open ? (labels.quickLinksClose || 'Close quick links') : (labels.quickLinksOpen || 'Open quick links'));
      this.panel.inert = !open;
      this.panel.setAttribute('aria-hidden', String(!open));
      if (open) {
        this.panel.hidden = false;
        // Commit the closed frame before the opening transition, including reopens.
        this.panel.getBoundingClientRect();
        this.dataset.open = 'true';
      } else {
        this.dataset.open = 'false';
        if (immediate || this.panel.hidden || matchMedia('(prefers-reduced-motion: reduce)').matches) this.finishClose();
        else this.hideTimer = setTimeout(() => this.finishClose(), 220);
      }
    }

    finishClose() {
      clearTimeout(this.hideTimer);
      if (!this.isOpen && this.panel) this.panel.hidden = true;
    }

    closeMenu(restoreFocus = false) {
      this.setOpen(false);
      if (restoreFocus) this.toggle?.focus({ preventScroll: true });
    }

    connectChat() {
      const chat = document.querySelector('shopify-chat');
      if (chat === this.chat) return;
      this.releaseChat();
      if (typeof chat?.show !== 'function') return;
      this.chat = chat;
      // Verified against the official Shopify chat Web Component (show/activator).
      chat.setAttribute('data-floating-tools-owner', this.id);
    }

    releaseChat() {
      if (this.chat?.getAttribute('data-floating-tools-owner') === this.id) this.chat.removeAttribute('data-floating-tools-owner');
      this.chat = null;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.setOpen(false, true);
      clearTimeout(this.hideTimer);
      this.chatObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.releaseChat();
      if (!document.querySelector('floating-tools')) document.documentElement.classList.remove('floating-tools-enabled');
      this.controller = null;
    }
  }
  customElements.define('floating-tools', FloatingTools);
}
